import { useState, useRef, useCallback } from 'react'
import { useTranslation } from 'react-i18next'

/**
 * SignaturePad — shared signature-capture control used everywhere a person signs something
 * in the app (contract, KYC/onboarding consent, invoice confirmation). Three modes:
 *   - type: a typed full name (always required; rendered in a cursive style)
 *   - draw: freehand on a canvas (mouse/touch/stylus)
 *   - upload: a photo/scan of a real signature (jpeg/png) — background is removed client-side
 *     so it composites onto a generated document like an actual ink signature, not a white box.
 *
 * onChange is called with { name, signatureDataUrl } — signatureDataUrl is a base64 PNG (or
 * null if only a typed name was given; the name alone is a valid, always-required signature).
 */

// Client-side background removal: sample the four corners of the image (almost always
// background, not the ink) and make near-that-color pixels transparent. This is deliberately
// simple (no ML dependency) — it works well for the common case of a signature photographed or
// scanned on a plain white/light sheet of paper, which is what the "upload" mode expects.
function removeBackground(img) {
  const canvas = document.createElement('canvas')
  canvas.width = img.naturalWidth
  canvas.height = img.naturalHeight
  const ctx = canvas.getContext('2d')
  ctx.drawImage(img, 0, 0)
  const { data, width, height } = ctx.getImageData(0, 0, canvas.width, canvas.height)

  // Sample the four corners to estimate the background color.
  const corners = [
    [0, 0], [width - 1, 0], [0, height - 1], [width - 1, height - 1],
  ].map(([x, y]) => {
    const i = (y * width + x) * 4
    return [data[i], data[i + 1], data[i + 2]]
  })
  const bg = corners.reduce((acc, c) => [acc[0] + c[0] / 4, acc[1] + c[1] / 4, acc[2] + c[2] / 4], [0, 0, 0])

  const THRESHOLD = 38  // color-distance tolerance — knock out anything close to the sampled background
  for (let i = 0; i < data.length; i += 4) {
    const dist = Math.sqrt(
      (data[i] - bg[0]) ** 2 + (data[i + 1] - bg[1]) ** 2 + (data[i + 2] - bg[2]) ** 2
    )
    if (dist < THRESHOLD) data[i + 3] = 0  // set alpha to 0 (transparent)
  }
  ctx.putImageData(new ImageData(data, width, height), 0, 0)
  return canvas.toDataURL('image/png')
}

function DrawCanvas({ value, onChange }) {
  const { t: tUI } = useTranslation('ui')
  const canvasRef = useRef(null)
  const drawing = useRef(false)
  const drew = useRef(false)

  const pos = (e) => {
    const c = canvasRef.current
    const r = c.getBoundingClientRect()
    const t = e.touches ? e.touches[0] : e
    return { x: (t.clientX - r.left) * (c.width / r.width), y: (t.clientY - r.top) * (c.height / r.height) }
  }
  const start = (e) => {
    e.preventDefault()
    drawing.current = true
    const ctx = canvasRef.current.getContext('2d')
    const p = pos(e)
    ctx.beginPath(); ctx.moveTo(p.x, p.y)
  }
  const move = (e) => {
    if (!drawing.current) return
    e.preventDefault()
    const ctx = canvasRef.current.getContext('2d')
    ctx.lineWidth = 2; ctx.lineCap = 'round'; ctx.strokeStyle = '#1e293b'
    const p = pos(e)
    ctx.lineTo(p.x, p.y); ctx.stroke()
    drew.current = true
  }
  const end = () => {
    if (!drawing.current) return
    drawing.current = false
    if (drew.current) onChange(canvasRef.current.toDataURL('image/png'))
  }
  const clear = () => {
    const c = canvasRef.current
    c.getContext('2d').clearRect(0, 0, c.width, c.height)
    drew.current = false
    onChange(null)
  }
  return (
    <div>
      <canvas
        ref={canvasRef}
        width={500} height={140}
        className="w-full h-[120px] bg-white border border-dashed border-gray-300 rounded-lg touch-none cursor-crosshair"
        onMouseDown={start} onMouseMove={move} onMouseUp={end} onMouseLeave={end}
        onTouchStart={start} onTouchMove={move} onTouchEnd={end}
      />
      <div className="flex items-center justify-between mt-1">
        <span className="text-[10px] text-gray-400">{value ? '✓ Signature captured' : 'Use a stylus, mouse, or finger.'}</span>
        <button type="button" onClick={clear} className="text-[11px] text-gray-500 hover:text-red-500 underline">{tUI('sig_clear')}</button>
      </div>
    </div>
  )
}

function UploadImage({ value, onChange }) {
  const { t: tUI } = useTranslation('ui')
  const [processing, setProcessing] = useState(false)
  const fileRef = useRef(null)

  const handleFile = useCallback((file) => {
    if (!file) return
    setProcessing(true)
    const reader = new FileReader()
    reader.onload = () => {
      const img = new Image()
      img.onload = () => {
        try {
          const cleaned = removeBackground(img)
          onChange(cleaned)
        } catch {
          // If canvas processing fails for any reason, fall back to the raw image rather
          // than blocking the signature entirely.
          onChange(reader.result)
        } finally {
          setProcessing(false)
        }
      }
      img.onerror = () => setProcessing(false)
      img.src = reader.result
    }
    reader.onerror = () => setProcessing(false)
    reader.readAsDataURL(file)
  }, [onChange])

  return (
    <div>
      {value ? (
        <div className="relative border border-gray-200 rounded-lg p-2 bg-[repeating-conic-gradient(#f3f4f6_0%_25%,white_0%_50%)] bg-[length:16px_16px]">
          <img src={value} alt="Your signature" className="max-h-[100px] mx-auto" />
          <button
            type="button"
            onClick={() => { onChange(null); if (fileRef.current) fileRef.current.value = '' }}
            className="absolute top-1 right-1 text-[11px] bg-white border border-gray-300 rounded px-1.5 py-0.5 text-gray-500 hover:text-red-600 hover:border-red-300"
          >
            Remove
          </button>
        </div>
      ) : (
        <label className="flex flex-col items-center justify-center h-[120px] border border-dashed border-gray-300 rounded-lg cursor-pointer hover:bg-gray-50 transition">
          {processing ? (
            <span className="text-xs text-gray-400">{tUI('sig_removing_bg')}</span>
          ) : (
            <>
              <span className="text-xs text-gray-500">📤 Upload a photo of your signature</span>
              <span className="text-[10px] text-gray-400 mt-1">{tUI('sig_format_hint')}</span>
            </>
          )}
          <input
            ref={fileRef}
            type="file"
            accept="image/jpeg,image/png,image/jpg"
            className="hidden"
            disabled={processing}
            onChange={(e) => handleFile(e.target.files?.[0])}
          />
        </label>
      )}
    </div>
  )
}

export default function SignaturePad({ name, onNameChange, signatureDataUrl, onSignatureChange, namePlaceholder = 'e.g. Abhi Consultant' }) {
  const { t: tUI } = useTranslation('ui')
  const [mode, setMode] = useState('type')  // 'type' | 'draw' | 'upload'

  return (
    <div>
      <label className="block mb-3">
        <span className="text-xs font-medium text-gray-600 block mb-1">{tUI('sig_type_name')}</span>
        <input
          type="text"
          value={name}
          onChange={(e) => onNameChange(e.target.value)}
          placeholder={namePlaceholder}
          className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
          style={{ fontFamily: name ? 'cursive' : 'inherit', fontStyle: name ? 'italic' : 'normal' }}
        />
        <span className="text-[10px] text-gray-400 mt-1 block">
          Your typed name, timestamp and IP are stored as your legal e-signature record. Required.
        </span>
      </label>

      <div className="mb-2">
        <span className="text-xs font-medium text-gray-600 block mb-1.5">{tUI('sig_add_image')}</span>
        <div className="flex gap-1 bg-white rounded-lg p-0.5 border border-gray-200 w-fit mb-2">
          {[
            { id: 'draw', label: '✏️ Draw' },
            { id: 'upload', label: '📷 Upload photo' },
          ].map(m => (
            <button
              key={m.id}
              type="button"
              // Clicking an already-selected mode toggles it off (back to name-only) and clears
              // any captured image — this replaces the old "None" button.
              onClick={() => {
                if (mode === m.id) { setMode('type'); onSignatureChange(null) }
                else setMode(m.id)
              }}
              className={`px-2.5 py-1 text-xs font-medium rounded-md transition ${mode === m.id ? 'bg-blue-600 text-white' : 'text-gray-600 hover:bg-gray-50'}`}
            >
              {m.label}
            </button>
          ))}
        </div>
        {mode === 'draw' && <DrawCanvas value={signatureDataUrl} onChange={onSignatureChange} />}
        {mode === 'upload' && <UploadImage value={signatureDataUrl} onChange={onSignatureChange} />}
        {mode === 'type' && <p className="text-[11px] text-gray-400">Optional — draw or upload a signature image, or just use your typed name above.</p>}
      </div>
    </div>
  )
}
